/** A promise wrapper over the two IndexedDB stores the app keeps.
 *
 *  IndexedDB rather than localStorage because a month of instances plus a queue
 *  of writes is structured data that has to survive a reload, and localStorage is
 *  synchronous, string-only and small. No library: this is four operations.
 *
 *  Everything here is best-effort. A private window, a browser with site data
 *  blocked, or a quota refusal all surface as a rejected request, and none of
 *  them should stop the app from working — every caller treats a failure as
 *  "nothing cached" and carries on against the network. */

const DB_NAME = 'hestia';
const DB_VERSION = 1;

/** API payloads, keyed by request. */
export const CACHE = 'cache';
/** Writes made while the server was out of reach, in the order they were made. */
export const OUTBOX = 'outbox';

let opening: Promise<IDBDatabase | null> | null = null;

const STORES = [CACHE, OUTBOX];

function create(db: IDBDatabase) {
  if (!db.objectStoreNames.contains(CACHE)) db.createObjectStore(CACHE);
  if (!db.objectStoreNames.contains(OUTBOX)) {
    db.createObjectStore(OUTBOX, { keyPath: 'seq', autoIncrement: true });
  }
}

function openAt(version?: number): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = version === undefined ? indexedDB.open(DB_NAME) : indexedDB.open(DB_NAME, version);
    } catch {
      return resolve(null); // storage disabled outright
    }
    req.onupgradeneeded = () => create(req.result);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
}

function wipe(): Promise<void> {
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.deleteDatabase(DB_NAME);
    } catch {
      return resolve();
    }
    // `onblocked` means another tab still holds it open. Give up rather than
    // wait: the app is perfectly usable with no cache, and that other tab has a
    // working database anyway.
    req.onsuccess = req.onerror = req.onblocked = () => resolve();
  });
}

async function open(): Promise<IDBDatabase | null> {
  if (opening) return opening;
  opening = (async () => {
    const db = await openAt(DB_VERSION);
    if (db && STORES.every((s) => db.objectStoreNames.contains(s))) return db;
    // Two ways to land here, both of them permanent if left alone. The database
    // can exist at the right version with no stores in it — anything that opens
    // it without asking for a version creates exactly that, and the upgrade then
    // never runs again. Or it can carry a *higher* version than this build knows
    // about, after someone ran a newer app and then went back, in which case
    // every open fails outright. Starting over costs nothing: a database in
    // either state holds no data to lose.
    db?.close();
    await wipe();
    return openAt(DB_VERSION);
  })();
  return opening;
}

function run<T>(
  store: string,
  mode: IDBTransactionMode,
  body: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  return open().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) return resolve(null);
        let req: IDBRequest<T>;
        try {
          req = body(db.transaction(store, mode).objectStore(store));
        } catch {
          return resolve(null);
        }
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      }),
  );
}

export const idb = {
  get: <T>(store: string, key: IDBValidKey) => run<T>(store, 'readonly', (s) => s.get(key) as IDBRequest<T>),
  put: <T>(store: string, value: T, key?: IDBValidKey) =>
    run(store, 'readwrite', (s) => s.put(value, key) as IDBRequest<IDBValidKey>),
  del: (store: string, key: IDBValidKey) =>
    run(store, 'readwrite', (s) => s.delete(key) as unknown as IDBRequest<undefined>),
  all: <T>(store: string) => run<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>),
  clear: (store: string) => run(store, 'readwrite', (s) => s.clear() as unknown as IDBRequest<undefined>),
};
